// hooks/useInventory.js
import {
  usePaginatedResource,
  useResourceItem,
  useCreateResource,
  useEditResource,
  useDeleteResource,
} from "./useCrudResource"

import {
  getInventories,
  getInventory,
  createInventory,
  editInventory,
  deleteInventory,
  getAllInventories,
  getTrashedInventories,
  restoreInventory,
  forceDeleteInventory,
  getInventoryBySlug,
  searchInventories,
  getAccessoriesList,
  getSendersList,
  getReceiversList,
  getVehiclesList,
  getInventoryCompanies,
} from "api/inventoryApi"

import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query"
import { useMemo } from "react"

export const INVENTORY_QUERY_KEY = "inventories"

// -------------------- FETCH PAGINATED --------------------
export const useInventories = (params = {}, enabled = true) => {
  return usePaginatedResource(
    INVENTORY_QUERY_KEY,
    getInventories,
    params,
    enabled
  )
}

// -------------------- FETCH SINGLE ITEM --------------------
export const useInventory = (id, enabled = true) => {
  return useResourceItem(INVENTORY_QUERY_KEY, getInventory, id, enabled)
}

// -------------------- FETCH BY SLUG --------------------
export const useInventoryBySlug = (slug, enabled = true) => {
  return useResourceItem(
    [INVENTORY_QUERY_KEY, "slug"],
    getInventoryBySlug,
    slug,
    enabled
  )
}

// -------------------- CREATE --------------------
export const useCreateInventory = () => {
  return useCreateResource(createInventory, INVENTORY_QUERY_KEY)
}

// -------------------- EDIT --------------------
export const useEditInventory = () => {
  return useEditResource(editInventory, INVENTORY_QUERY_KEY)
}

// -------------------- DELETE --------------------
export const useDeleteInventory = () => {
  return useDeleteResource(deleteInventory, INVENTORY_QUERY_KEY)
}

// -------------------- FORCE DELETE --------------------
export const useForceDeleteInventory = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: forceDeleteInventory,
    onSuccess: (_, id) => {
      queryClient.invalidateQueries([INVENTORY_QUERY_KEY])
      queryClient.removeQueries([INVENTORY_QUERY_KEY, id])
    },
  })
}

// -------------------- ADDITIONAL --------------------

// Get all inventories without pagination
export const useAllInventories = (params = {}) => {
  return usePaginatedResource(
    INVENTORY_QUERY_KEY,
    getAllInventories,
    { ...params, all: true },
    true
  )
}

// Get trashed inventories
export const useTrashedInventories = (params = {}) => {
  return usePaginatedResource(
    [INVENTORY_QUERY_KEY, "trashed"],
    getTrashedInventories,
    params,
    true
  )
}

// Restore a trashed inventory
export const useRestoreInventory = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: restoreInventory,
    onSuccess: (res, { id }) => {
      const restoredItem = res?.data
      if (!restoredItem) return

      queryClient.setQueriesData([INVENTORY_QUERY_KEY, "all"], old => {
        if (!old || !old.data || !Array.isArray(old.data.data)) return old
        const updated = structuredClone(old)
        updated.data.data = [restoredItem, ...updated.data.data]
        updated.data.total = (updated.data.total || 0) + 1
        return updated
      })

      queryClient.setQueryData([INVENTORY_QUERY_KEY, id], restoredItem)
    },
  })
}

// -------------------- SEARCH --------------------
export const useSearchInventories = (
  searchTerm,
  params = {},
  enabled = true
) => {
  return usePaginatedResource(
    INVENTORY_QUERY_KEY,
    searchInventories,
    { ...params, search: searchTerm },
    enabled
  )
}

// -------------------- RELATED LISTS (v5 syntax) --------------------

// Fetch accessories list
export const useGetAccessoriesList = () =>
  useQuery({
    queryKey: ["inventory-accessories"],
    queryFn: getAccessoriesList,
  })

// Fetch senders list
export const useGetSendersList = () =>
  useQuery({
    queryKey: ["inventory-senders"],
    queryFn: getSendersList,
  })

// Fetch receivers list
export const useGetReceiversList = () =>
  useQuery({
    queryKey: ["inventory-receivers"],
    queryFn: getReceiversList,
  })

// Fetch vehicles list
export const useGetVehiclesList = () =>
  useQuery({
    queryKey: ["inventory-vehicles"],
    queryFn: getVehiclesList,
  })

// Fetch company list
export const useGetCompaniesList = (params = {}) => {
  return useQuery({
    queryKey: ["vehicle-stock-inventory-companies", params],
    queryFn: () => getInventoryCompanies(params),
    keepPreviousData: true, // smooth pagination/filter changes
  })
}

// -------------------- FORMATTING / OPTIONS --------------------
export const useInventoryInfo = (rawData = {}) => {
  const inventory = rawData?.inventory ?? {}

  const options = useMemo(
    () => ({
      features: inventory?.features ?? [],
      accessories: inventory?.accessories ?? [],
      senders: inventory?.senders ?? [],
      receivers: inventory?.receivers ?? [],
      vehicles: inventory?.vehicles ?? [],
    }),
    [inventory]
  )

  const getInventoryInfo = (inv = {}) => ({
    ...inv,
    name: inv.name || "-",
    vehicle: inv.vehicle?.name || "-",
    sender: inv.sender?.name || "-",
    receiver: inv.receiver?.name || "-",
    items: inv.items || [],
    remarks: inv.remarks || "-",
  })

  return { inventory, options, getInventoryInfo }
}

// -------------------- SEARCH RELATED LISTS --------------------

// Search senders
export const useSearchSenders = (searchTerm, params = {}, enabled = true) =>
  useQuery({
    queryKey: ["inventory-senders-search", searchTerm],
    queryFn: () => getSendersList({ ...params, search: searchTerm }),
    enabled,
  })

// Search receivers
export const useSearchReceivers = (searchTerm, params = {}, enabled = true) =>
  useQuery({
    queryKey: ["inventory-receivers-search", searchTerm],
    queryFn: () => getReceiversList({ ...params, search: searchTerm }),
    enabled,
  })

// Search vehicles
export const useSearchVehicles = (searchTerm, params = {}, enabled = true) =>
  useQuery({
    queryKey: ["inventory-vehicles-search", searchTerm],
    queryFn: () => getVehiclesList({ ...params, search: searchTerm }),
    enabled,
  })
